import {
	getGoalAndInterventionDefinitionLibraryOptions,
	getGoalsAndInterventionsByCertificationPeriodOptions,
	type FullOrderViewModel
} from '@generated';
import { useVisit } from '~/features/visits/VisitContext';
import { Goal } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '~/shared/components/buttons/Button';
import { getSuggestedGoalsAndInterventionsForVisit } from './suggestions';
import { convertClientDateToServerDate } from '~/util/date';
import { GoalAndInterventionsWizardFormModal } from './wizard/GoalAndInterventionsWizardFormModal';

export function GoalsAndInterventionsForVisit({ fullOrder }: { fullOrder: FullOrderViewModel }) {
	const { id, visitFindings } = useVisit();
	const { data: existingGoalsAndInterventions, isLoading: existingGoalsAndInterventionsLoading } = useQuery(
		getGoalsAndInterventionsByCertificationPeriodOptions(fullOrder.certificationPeriodId)
	);
	const { data: goalAndInterventionDefinitionLibrary, isLoading: goalAndInterventionDefinitionLibraryLoading } = useQuery(
		getGoalAndInterventionDefinitionLibraryOptions(convertClientDateToServerDate(fullOrder.certificationPeriod.startDate))
	);

	if (existingGoalsAndInterventionsLoading || goalAndInterventionDefinitionLibraryLoading) {
		return null;
	} else if (!existingGoalsAndInterventions || !goalAndInterventionDefinitionLibrary) {
		throw new Error('Failed to load goals and interventions for visit');
	} else if (!fullOrder.invariants.canBeEdited) {
		return null;
	}

	const allGoals = [...fullOrder.goals, ...existingGoalsAndInterventions.goals];
	const allInterventions = [...fullOrder.interventions, ...existingGoalsAndInterventions.interventions];

	const suggestedGoalsAndInterventions = getSuggestedGoalsAndInterventionsForVisit(
		visitFindings,
		goalAndInterventionDefinitionLibrary,
		allGoals,
		allInterventions
	);

	return (
		<Button
			aria-label="View Suggested Goals/Interventions"
			Icon={Goal}
			intent="neutral"
			statusIndicator={suggestedGoalsAndInterventions.length > 0 ? 'success' : undefined}
			useSuspense
			renderModal={closeModal => (
				<GoalAndInterventionsWizardFormModal
					suggestedGoalsAndInterventions={suggestedGoalsAndInterventions}
					fullOrder={fullOrder}
					closeModal={closeModal}
				/>
			)}
		/>
	);
}
